import { fromJS, List, Map } from 'immutable';
import { browserHistory } from 'react-router';

import {
  getIsStaff,
  getPreSelectedService,
  getServiceById,
  getServiceId,
  getTaxCategoryById,
  selectEPOSIntegrated,
  selectServices,
} from 'selectors/root';
import { selectProductsInCartIds } from 'selectors/order';
import { selectSetAllergenFilters, selectSetLifestyleFilters } from 'selectors/allergenFilter';
import { isStockManaged, shouldShowPriceExcludingTax, shouldShowGuestDirectory } from 'selectors/features';
import {
  filterActiveCategoriesByServices,
  filterServicesByCategory,
} from 'components/Pages/Browse/filterCategoryServices';
import { REPORTING_CAT_BEVERAGES, REPORTING_CAT_FOOD } from 'appConstants';
import { getLocale } from 'selectors/user';
import { comboGroupToModifierTransformer } from 'utils/comboGroupToModifierTransformer';
import { evaluateSchedule, venueCurrentTime, findScheduleById } from 'utils/scheduleEvaluator';
import { convertVenueTimezoneToUtc } from 'utils/date';

const evaluateEntitySchedule = (state, entity) => {
  if (!entity) return { active: false, nextActiveTime: null, nextInactiveTime: null, schedule: null };
  const scheduleId = entity.get('scheduleId');
  if (scheduleId === null || typeof scheduleId === 'undefined') {
    return { active: true, nextActiveTime: null, nextInactiveTime: null, schedule: null }; // no scheduleId = always active
  }
  const schedule = findScheduleById(state.getIn(['browse', 'data', 'schedules']), scheduleId);
  return { ...evaluateSchedule(schedule, venueCurrentTime(state.getIn(['venue', 'timezone']))), schedule };
};

// For day-of-week schedules, getNextActiveTime returns a "shifted" timestamp (venue
// wall-clock encoded in device-local fields). Convert it back to a real UTC instant so
// display components (which apply the venue timezone themselves) render correctly.
// date_start-only schedules already return a real instant, so they're left untouched.
const scheduleHasDays = schedule => !!schedule && !!schedule.get('days') && schedule.get('days').size > 0;

const displayNextActiveTime = (state, evaluation) => {
  const { nextActiveTime, schedule } = evaluation;
  if (nextActiveTime === null || !scheduleHasDays(schedule)) return nextActiveTime;
  return convertVenueTimezoneToUtc(new Date(nextActiveTime), state.getIn(['venue', 'timezone'])).getTime();
};

export const menuNoLongerActive = (state, menu) => {
  const { active, nextActiveTime } = evaluateEntitySchedule(state, menu);
  return !active && !nextActiveTime;
};

/**
 * Browse state selectors
 */

export const isMenuActive = (state, menuId, serviceId) => {
  // is menu active for menu and service schedules
  const menu = getMenuById(state, menuId);
  if (!menu) return false;
  const menuActive = evaluateEntitySchedule(state, menu).active;
  if (!serviceId) return menuActive;
  return isServiceActiveById(state, serviceId) && menuActive;
};

export const isMenuAvailable = (state, menuId, serviceId) => {
  // is menu active and enabled
  const menu = getMenuById(state, menuId);
  if (!menu) return false;
  return menu.get('enabled') && isMenuActive(state, menuId, serviceId);
};

export const isMenuVisible = (state, menuId, serviceId) => {
  const menu = getMenuById(state, menuId);
  if (!menu || !serviceId) return false;

  const service = getServiceById(state, serviceId);

  const hideOutOfScheduleEnabled = Boolean(service.get('hide_out_of_schedule_menus'));
  const outOfSchedule = isMenuOutOfSchedule(state, menu);

  if (hideOutOfScheduleEnabled && outOfSchedule) return false;

  return (
    menu.get('enabled') &&
    !hideStaffMenu(state, menu.get('id')) &&
    menuIsOnService(menu, serviceId) &&
    !menuNoLongerActive(state, menu) &&
    menuHasProducts(state, menu, service)
  );
};

// Inactive with a future activation; contrast menuNoLongerActive which has none.
export const isMenuOutOfSchedule = (state, menu) =>
  evaluateEntitySchedule(state, menu).nextActiveTime !== null;

// Active if any schedule is active; no schedules (or all missing) defers to the parent menu.
export const computeSectionAvailability = (state, section) => {
  const scheduleIds = section?.get('schedules');
  if (!scheduleIds || scheduleIds.size === 0) return { active: true, nextActiveTime: null };

  const allSchedules = state.getIn(['browse', 'data', 'schedules']);
  const currentTime = venueCurrentTime(state.getIn(['venue', 'timezone']));

  const evaluations = scheduleIds
    .toArray()
    .map(id => findScheduleById(allSchedules, id))
    .filter(Boolean)
    .map(schedule => ({ schedule, ...evaluateSchedule(schedule, currentTime) }));

  // No schedules resolved (e.g. still loading) → defer to the parent menu.
  if (evaluations.length === 0) return { active: true, nextActiveTime: null };

  if (evaluations.some(evaluation => evaluation.active)) return { active: true, nextActiveTime: null };

  const earliest = evaluations
    .filter(evaluation => evaluation.nextActiveTime)
    .reduce((soonest, evaluation) => {
      if (!soonest || evaluation.nextActiveTime < soonest.nextActiveTime) return evaluation;
      return soonest;
    }, null);

  // displayNextActiveTime converts the day-schedule "shifted" boundary back to a real UTC instant.
  return { active: false, nextActiveTime: earliest ? displayNextActiveTime(state, earliest) : null };
};

// Inactive section whose schedules will never re-activate (e.g. end date in the past);
// the section analogue of menuNoLongerActive.
export const sectionNoLongerActive = (state, section) => {
  const { active, nextActiveTime } = computeSectionAvailability(state, section);
  return !active && !nextActiveTime;
};

export const hideStaffMenu = (state, menuId) => {
  const menu = getMenuById(state, menuId);
  return menu?.get('staffOnly') && !getIsStaff(state);
};

export const getMenuNextActiveDetails = (state, menuId, serviceId) => {
  const menu = getMenuById(state, menuId);
  if (!menu) return false;
  const service = getServiceById(state, serviceId);

  const serviceEval = service ? evaluateEntitySchedule(state, service) : null;
  const serviceNext = serviceEval ? serviceEval.nextActiveTime : null;
  const menuEval = evaluateEntitySchedule(state, menu);
  const menuNext = menuEval.nextActiveTime;

  if (!serviceNext && !menuNext) return false;

  const useService = !!serviceNext;
  return {
    time: displayNextActiveTime(state, useService ? serviceEval : menuEval),
    type: useService ? 'service' : 'menu',
  };
};

// Defaults to true while the section is loading or has no schedules.
export const getSectionScheduleActive = (state, menuId, sectionId) => {
  const section = getMenuSectionById(state, menuId, sectionId);
  if (!section || !section.size) return true;
  return computeSectionAvailability(state, section).active;
};

export const isSectionActive = (state, menuId, sectionId, serviceId) =>
  isMenuActive(state, menuId, serviceId) && getSectionScheduleActive(state, menuId, sectionId);

// Returns when both menu and section are next open together, taking the later of the two times.
export const getSectionNextActiveDetails = (state, menuId, sectionId, serviceId) => {
  const menuDetails = getMenuNextActiveDetails(state, menuId, serviceId);
  const section = getMenuSectionById(state, menuId, sectionId);
  const sectionNextActive = section ? computeSectionAvailability(state, section).nextActiveTime : null;

  if (!menuDetails) {
    return sectionNextActive ? { time: sectionNextActive, type: 'section' } : false;
  }

  if (sectionNextActive && sectionNextActive > menuDetails.time) {
    return { time: sectionNextActive, type: 'section' };
  }

  return { ...menuDetails };
};

export const isServiceActiveById = (state, serviceId) => {
  const service = getServiceById(state, serviceId);
  if (!service || !service.size) return false;
  return evaluateEntitySchedule(state, service).active;
};

export const getServiceNextActiveTime = (state, serviceId) => {
  const service = getServiceById(state, serviceId);
  if (!service || !service.size) return null;
  return displayNextActiveTime(state, evaluateEntitySchedule(state, service));
};

export const menuIsLoading = state => state.getIn(['browse', 'isLoading']);

export const sectionIsLoading = state => state.getIn(['browse', 'sectionIsLoading']);

export const selectMenuTypes = state => state.getIn(['browse', 'data', 'menuTypes'], new List());

export const selectMenuTypeById = (state, id) => {
  const menuTypes = selectMenuTypes(state);
  return menuTypes.find(menuType => menuType.get('id') === parseInt(id, 10)) || new Map();
};

export const selectMenus = state => state?.getIn?.(['browse', 'data', 'menus']);

export const selectVisibleMenus = (state, serviceId) => {
  const allMenus = selectMenus(state) || new Map();
  return allMenus.filter(menu => isMenuVisible(state, menu.get('id'), serviceId));
};

export const selectProducts = state => state.getIn(['browse', 'data', 'products'], new Map());

export const getProductById = (state, productId) =>
  selectProducts(state).get(productId?.toString(), new Map());

export const noFoodInCart = state => {
  const productsInCartIds = selectProductsInCartIds(state);

  return !productsInCartIds
    .map(id => getProductById(state, id).get('reporting_category'))
    .some(reportingId => reportingId === REPORTING_CAT_FOOD);
};

export const getMenuTypes = (state, menuId) => {
  const menus = selectMenus(state);
  if (!menus) return false;

  const menu = menus.find((menu, id) => menuId === id);
  return !!menu && menu.get('types');
};

export const menuHasMenuType = (state, menuId, menuTypeId) => {
  const menuTypes = getMenuTypes(state, menuId);
  return !!menuTypes && !!menuTypes.find(id => id === menuTypeId);
};

export const menuIsOnService = (menu, serviceId) => {
  if (!menu.get('services') || (menu.get('services') && menu.get('services').size === 0)) return true;
  return !!menu.get('services').find(menuService => menuService === parseInt(serviceId, 10));
};

export const menusOnService = (menus, serviceId) => menus.filter(menu => menuIsOnService(menu, serviceId));

export const selectChildSections = (state, menuId, sectionId) => {
  if (!menuId || !sectionId) return new List();
  const menu = getMenuById(state, menuId);
  return menu && menu.get('sections')
    ? menu
        .get('sections')
        .filter(
          childSection =>
            childSection.get('parent_id') === parseInt(sectionId, 10) &&
            !sectionNoLongerActive(state, childSection)
        )
        .toList()
        .sortBy(section => section.get('sort_order'))
    : new List();
};

export const selectCurrentSectionAndChildSections = (state, menuId, sectionId, serviceId) => {
  const section = getMenuSectionById(state, menuId, sectionId);
  const childSections = selectChildSections(state, menuId, sectionId, serviceId);

  return [...childSections, section].sort((a, b) => a.get('sort_order') - b.get('sort_order'));
};

export const getProductBreadcrumbs = (state, menuId, sectionId) => {
  const menu = getMenuById(state, menuId);
  const section = getMenuSectionById(state, menuId, sectionId);
  const menuName = menu?.get('translations')?.get(getLocale(state))?.get('name') ?? menu?.get('name');
  const sectionName = section?.get('name');
  let parentSection;
  if (section?.get('parent_id')) {
    parentSection = getMenuSectionById(state, menuId, section.get('parent_id'))?.get('name');
  }
  const breadcrumbs = [menuName, parentSection, sectionName];
  return breadcrumbs.filter(Boolean);
};

export const getProductStockQuantity = (state, productId) => {
  const stockManaged = isStockManaged(state);
  if (!stockManaged) return null;
  const product = getProductById(state, productId);
  return product?.get('quantity_in_stock', null);
};

export const productOutOfStock = (state, productId) => {
  const stockManaged = isStockManaged(state);
  const product = getProductById(state, productId);
  return stockManaged && product?.get('quantity_in_stock') === 0;
};

export const productIsEnabled = (product, service) => {
  if (!product || !product.size) return true; // if menu doesn't include product details just return true
  if (!product.get('enabled') || product.get('snoozed')) return false;
  if (product.get('combo') && product.get('combo').get('is_orderable') === false) return false;
  if (service && service.get('epos_integrated') && !product.get('has_epos_link')) return false;
  if (!product.get('services') || product.get('services').size === 0) return true;
  return product.get('services').contains(parseInt(service.get('id'), 10));
};

export const itemIsEnabled = (state, item, service) => {
  const product = getProductById(state, item.get('productId'));
  return item.get('enabled') && productIsEnabled(product, service);
};

export const selectSectionHeaderImage = (state, sectionId, menuId) => {
  const section = getMenuSectionById(state, menuId, sectionId);
  const sectionImage = section?.getIn(['images', 0, 'path']);
  if (sectionImage) return sectionImage;
  const menu = getMenuById(state, menuId);
  if (section?.get('parent_id')) {
    const parentSection = getMenuSectionById(state, menuId, section.get('parent_id'));
    if (parentSection?.getIn(['images', 0, 'path'])) return parentSection?.getIn(['images', 0, 'path']);
  }
  return menu?.getIn(['images', 0, 'path']);
};

export const selectSectionProducts = (state, section, service) => {
  if (!section) return new List();

  return section
    .get('section_products')
    .filter(item => itemIsEnabled(state, item, service))
    .sortBy(item => item.get('id'))
    .sortBy(item => item.get('sort_order'))
    .sortBy(item => productIsFiltered(state, item.get('productId')));
};

export const sectionHasProducts = (state, section, service) =>
  !!selectSectionProducts(state, section, service).size;

export const isParentSection = (parent, menu) =>
  menu
    .get('sections')
    .reduce((carry, section) => carry || section.get('parent_id') === parent.get('id'), false);

export const selectSectionsWithProducts = (state, menu, service) => {
  if (!menu?.size || !menu.get('sections').size) return new List();
  return menu
    .get('sections')
    .filter(
      section =>
        (isParentSection(section, menu) ||
          (section.get('parent_id') === null && sectionHasProducts(state, section, service))) &&
        !sectionNoLongerActive(state, section)
    )
    .toList()
    .sortBy(section => section.get('sort_order'));
};

export const selectSections = (state, menu, service) =>
  selectSectionsWithProducts(state, menu, service).sortBy(section => sectionIsFiltered(state, section));

export const menuHasProducts = (state, menu, service) => {
  if (!menu.get('sections').size) return false;
  return !!menu.get('sections').find(section => sectionHasProducts(state, section, service));
};

export const selectMenusByType = (state, menuTypeId, serviceId) => {
  if (!menuTypeId || !serviceId) return new Map();

  const ofType = selectVisibleMenus(state, serviceId).filter(menu =>
    menu.get('types').find(id => id === parseInt(menuTypeId, 10))
  );

  // Evaluate each menu's schedule once up front rather than on every sort comparison.
  const activeSortKey = {};
  ofType.forEach(menu => {
    activeSortKey[menu.get('id')] = evaluateEntitySchedule(state, menu).active ? 0 : 1;
  });

  return ofType
    .sortBy(menu => menu.get('sort_order'))
    .sortBy(menu => menuIsFiltered(state, menu))
    .sortBy(menu => activeSortKey[menu.get('id')]);
};

export const selectActiveMenus = (state, menuTypeId, serviceId) => {
  if (selectMenus(state) === undefined) return undefined;
  return selectMenus(state).filter(menu => {
    if (!isMenuAvailable(state, menu.get('id'), serviceId) || !menuIsOnService(menu, serviceId)) return false;
    return menu
      .get('types')
      .reduce((hasType, typeId) => hasType || typeId === parseInt(menuTypeId, 10), false);
  });
};

export const selectVenueCategoriesDetails = state => state.getIn(['venue', 'service_categories']);

export const selectNormalizedServices = state => {
  const fullCategoriesDetails = selectVenueCategoriesDetails(state);
  const services = selectServices(state);
  const normalisedServices = services.map(service => {
    const normalisedServiceCategories =
      service.get('categories') &&
      service.get('categories').map(id => fullCategoriesDetails.find(cat => cat.get('id') === id));
    return service.set('categories', normalisedServiceCategories);
  });
  return normalisedServices.toList().toJS();
};

const getServiceMenuTypes = (state, service) =>
  service.fulfilment_methods &&
  service.fulfilment_methods
    .map(method => method.menuTypeIds)
    .reduce((arr, menuTypesList) => arr.concat(menuTypesList), [])
    .reduce((menuTypes, menuTypeId) => {
      if (!menuTypes.find(type => type.id === menuTypeId)) {
        const menuType = selectMenuTypeById(state, menuTypeId);
        menuTypes.push(menuType.toJS());
      }
      return menuTypes;
    }, []);

export const selectServicesForMenuType = (state, menuTypeId = null) => {
  const services = selectNormalizedServices(state)
    .sort((a, b) => a.sort_order - b.sort_order)
    .map(service => ({ ...service, menu_types: getServiceMenuTypes(state, service) }));

  if (!menuTypeId) return services;

  return services.filter(
    service => !menuTypeId || (menuTypeId && service.menu_types.find(menuType => menuType.id === menuTypeId))
  );
};

export const menusAreFiltered = (state, menuTypeId, serviceId) =>
  selectActiveMenus(state, menuTypeId, serviceId).some(menu => menuIsFiltered(state, menu));

export const menuHasFilteredSections = (state, menu) =>
  menu?.get('sections').some(section => sectionIsFiltered(state, section));

export const menuIsFiltered = (state, menu) =>
  !!menu.get('sections').size &&
  !menu.get('sections').filter(section => !sectionIsFiltered(state, section)).size;

export const sectionHasFilteredProducts = (state, section) =>
  section.get('section_products').some(item => productIsFiltered(state, item.get('productId')));

export const sectionIsFiltered = (state, section) =>
  !!section.get('section_products').size &&
  !section.get('section_products').filter(item => !productIsFiltered(state, item.get('productId'))).size;

export const productIsFiltered = (state, productId) => {
  const product = getProductById(state, productId);
  if (!product.size) return false;
  const allergens = product.get('allergens');

  const allergenFilters = selectSetAllergenFilters(state);
  const allergenFiltersAreSet = allergenFilters && allergenFilters.length;

  if (allergenFiltersAreSet && allergens) {
    const hiddenByAllergen = allergenFilters.reduce(
      (hidden, filterKey) => (hidden === true ? true : allergens.includes(filterKey) === true),
      false
    );
    if (hiddenByAllergen) return true;
  }

  const lifestyleFilters = selectSetLifestyleFilters(state);
  const lifestyleFiltersAreSet = lifestyleFilters && lifestyleFilters.length;

  if (lifestyleFiltersAreSet) {
    return lifestyleFilters.reduce(
      (hidden, filterKey) => (hidden === true ? true : !product.get(filterKey)),
      false
    );
  }

  return false;
};

export const productIsFoodOrBeverage = product =>
  [REPORTING_CAT_BEVERAGES, REPORTING_CAT_FOOD].includes(product.get('reporting_category'));

export const getMenuById = (state, menuId) =>
  typeof selectMenus(state) === 'undefined'
    ? undefined
    : selectMenus(state).find((menu, id) => menuId && id === menuId.toString());

export const getMenuSectionById = (state, menuId, sectionId) => {
  sectionId = sectionId?.toString();
  return typeof getMenuById(state, menuId) === 'undefined'
    ? undefined
    : getMenuById(state, menuId).getIn(['sections', sectionId]);
};

export const getScheduleById = (state, id) =>
  findScheduleById(state.getIn(['browse', 'data', 'schedules']), id) || new Map();

export const getBackButton = state => state.getIn(['browse', 'backButton']);

export const selectBrowseInfoTitle = state => state.getIn(['browse', 'browseInfo', 'title'], '');
export const selectBrowseInfoDescription = state => state.getIn(['browse', 'browseInfo', 'description'], '');
export const selectBrowseInfoImagePath = state => state.getIn(['browse', 'browseInfo', 'imagePath'], '');

export const selectDefaultBackButton = () => ({ text: 'home', href: '/' });

export const selectGetStartedLink = (state, menuTypeId = null) => {
  let services = selectServicesForMenuType(state, menuTypeId);
  const serviceCategories = filterActiveCategoriesByServices(services);
  const link = serviceCategories.length > 1 ? '/service-categories' : '/services';
  const preSelectedService = getPreSelectedService(state);
  if (preSelectedService) services = services.filter(service => service.id === preSelectedService);
  const guestDirectoryEnabled = shouldShowGuestDirectory(state);

  if (services.length === 0 && guestDirectoryEnabled) {
    return '/guest-directory';
  }

  if (services.length === 1 && !guestDirectoryEnabled) {
    return `/service/${services[0].id}`;
  }

  return link;
};
export const selectGetStartedServiceId = (state, menuTypeId = null) => {
  let services = selectServicesForMenuType(state, menuTypeId);
  const preSelectedService = getPreSelectedService(state);
  if (preSelectedService) services = services.filter(service => service.id === preSelectedService);
  const service = services.length === 1 ? services[0].id : undefined;
  return service;
};
export const selectMenuSelectionBackButton = (state, serviceId) => {
  const services = selectServicesForMenuType(state, selectMenuTypeId(state));
  const service = getServiceById(state, serviceId);
  if (service.size) {
    if (!service.get('categories').size && services.length > 1) {
      return { text: 'services', href: '/services' };
    }
    if (service.get('categories').size) {
      const servicesInCategory = filterServicesByCategory(service.getIn(['categories', 0]), services);
      if (servicesInCategory.length === 1) {
        return { text: 'categories', href: '/service-categories' };
      }
      return {
        text: 'services',
        href: `/service-categories/${service.getIn(['categories', 0]).toString()}`,
      };
    }
  }
  return selectDefaultBackButton();
};

export const selectMenuBackButton = (state, serviceId) => {
  const menuTypeId = selectMenuTypeId(state);
  const menus = selectMenusByType(state, menuTypeId, serviceId);
  if (menus && menus.size > 1) return { text: 'menus', href: `/service/${serviceId}` };
  return selectMenuSelectionBackButton(state, serviceId);
};

export const selectSectionBackButton = (state, serviceId, menuId, sectionId) => {
  const menu = getMenuById(state, menuId);
  const sections = selectSectionsWithProducts(state, menu, getServiceById(state, serviceId));
  if (menu.getIn(['sections', sectionId, 'parent_id']))
    return {
      text: 'section',
      href: `/service/${serviceId}/menu/${menuId}/section/${menu.getIn([
        'sections',
        sectionId,
        'parent_id',
      ])}`,
    };
  if (sections.size > 1) return { text: 'menu', href: `/service/${serviceId}/menu/${menuId}/` };
  return selectMenuBackButton(state, serviceId);
};

export const selectProductBackButton = (state, serviceId, menuId, sectionId) => ({
  text: 'section',
  href: `/service/${serviceId}/menu/${menuId}/section/${sectionId}`,
});

export const selectItemBackButton = (state, item) => {
  const serviceId = getServiceId(state);
  return `/service/${serviceId}/menu/${item.get('menu_id') || item.get('menuId')}/section/${
    item.get('section_id') || item.get('sectionId')
  }`;
};

export const backToItemMenuPage = (state, item) =>
  browserHistory.push({
    pathname: selectItemBackButton(state, item),
  });

export const productHasModifiers = (product, exclBaseModifier = false) => {
  const modifierGroups = product?.get('modifier_groups');

  if (modifierGroups !== undefined && !modifierGroups.isEmpty()) {
    const hasModifiers = modifierGroups.some(modifierGroup => !modifierGroup.get('modifiers').isEmpty());
    if (hasModifiers && exclBaseModifier) {
      const groups = modifierGroups.filter(modifierGroup =>
        modifierGroup.get('modifiers').find(modifier => modifier.get('display_type') !== 'base')
      );
      return !!groups.size;
    }
    return hasModifiers;
  }
  return false;
};

export const productHasCombo = (product = false) => {
  const combo = product?.get('combo') ?? null;
  if (combo === null) return false;
  const comboGroups = combo.get('combo_groups');
  if (comboGroups === undefined || comboGroups.isEmpty()) return false;
  return comboGroups.some(comboGroup => !comboGroup.get('combo_group_products').isEmpty());
};

// Helper to check if combo has defaults in every combo group
const comboHasRequiredDefaults = combo => {
  const comboGroups = combo?.get('combo_groups');
  if (!comboGroups || comboGroups.isEmpty()) return false;

  let allGroupsHaveDefault = true;

  comboGroups.forEach(comboGroup => {
    const products = comboGroup.get('combo_group_products');
    if (!products || products.isEmpty() || !products.some(product => product.get('default') === true)) {
      allGroupsHaveDefault = false;
    }
  });

  return allGroupsHaveDefault;
};

const hasRequiredModifiersWithoutDefaults = modifierGroups => {
  let missingDefaults = false;
  modifierGroups.forEach(modifierGroup =>
    modifierGroup.get('modifiers').forEach(modifier => {
      const required = modifier.get('required');
      const hasDefaultValue = modifier.get('values').some(value => value.get('default_value'));
      if (required && !hasDefaultValue) missingDefaults = true;
    })
  );
  return missingDefaults;
};

export const showInstantAddButton = product => {
  const modifierGroups = product.get('modifier_groups') || List();
  const combo = product.get('combo');

  if (modifierGroups.isEmpty() && !combo) {
    return true;
  }

  let modifiersHaveRequiredDefaults = true;

  // for a combo, we can show the Add button if every group in the combo has a default product
  const comboHasDefaults = combo ? comboHasRequiredDefaults(combo) : true;

  // for a product with modifiers, show the Add button if all modifiers have default values
  if (!modifierGroups.isEmpty()) {
    modifiersHaveRequiredDefaults = !hasRequiredModifiersWithoutDefaults(modifierGroups);
  }

  return modifiersHaveRequiredDefaults && comboHasDefaults;
};

/**
 * Given a modifier, return the default value if it exists, otherwise return an empty array.
 */
const getDefaultValues = modifier => {
  const values = modifier.get('values');
  const explicitDefaults = values.filter(v => v.get('default_value')).toJS();

  if (explicitDefaults.length) return explicitDefaults;

  // if a base modifier with no explicit defaults exists, return the first value
  if (modifier.get('display_type') === 'base' && values.size > 0) {
    return [values.first().toJS()];
  }

  return [];
};

/**
 * For a given product, return a list of modifiers across all modifier groups if the modifier has a default value.
 */
export const selectDefaultModifiers = product => {
  const modifierGroups = product?.get('modifier_groups');
  if (!modifierGroups) return;

  const modifiers = [];

  modifierGroups.forEach(modifierGroup => {
    modifierGroup.get('modifiers').forEach(modifier => {
      const values = getDefaultValues(modifier);

      // if we have resolved value, return the modifer with these defaults
      if (values.length > 0) {
        modifiers.push(fromJS({ group: modifierGroup.get('id'), modifier, values }));
      }
    });
  });

  // product may be a combo, in which case add combo groups as modifiers too, IF they have defaults
  const comboGroups = getProductComboGroupsAsModifiers(product);
  if (comboGroups) {
    comboGroups.forEach(modifier => {
      const values = getDefaultValues(fromJS(modifier));
      // if we have resolved value, return the modifier with these defaults
      if (values.length > 0) {
        modifiers.push(fromJS({ combo_group: modifier.id, modifier, values }));

        // default values that are products may themselves have modifiers with defaults, so recurse
        values
          .filter(value => value.product)
          .forEach(value => {
            const additionalModifiers = selectDefaultModifiers(fromJS(value.product));
            if (additionalModifiers) modifiers.push(...additionalModifiers);
          });
      }
    });
  }

  return modifiers.length ? fromJS(modifiers) : undefined;
};

export const getProductComboGroupsAsModifiers = product => {
  if (!product.get('combo')) return;
  return product
    .get('combo')
    .get('combo_groups')
    .sortBy(group => group.get('sort_order'))
    .map(cg => comboGroupToModifierTransformer(cg.toJS()))
    .filter(modifier => !!modifier?.id);
};

/**
 * Filter modifier values when EPOS is integrated.
 * Ensures only values whose product has has_epos_link = true remain
 * (indicating the product is linked to an epos product)
 */
export const filterModifierValues = (modifiers, isEposIntegrated) => {
  if (!isEposIntegrated) return modifiers;

  return modifiers.map(mod => ({
    ...mod,
    values: mod.values?.filter(v => v.product?.has_epos_link) || [],
  }));
};

// Same service rule as productIsEnabled, applied to modifier options: an option
// backed by a service-limited product must only appear on its allowed services.
export const filterModifierValuesByService = (modifiers, serviceId) => {
  if (!serviceId) return modifiers;
  const id = parseInt(serviceId, 10);
  return modifiers.map(mod => ({
    ...mod,
    values:
      mod.values?.filter(v => {
        const services = v.product?.services;
        return !services?.length || services.includes(id);
      }) || [],
  }));
};

export const getProductModifiers = (
  product,
  inclBaseModifier = false,
  selectedModifiers = [],
  isEposIntegrated = false,
  serviceId = null
) => {
  const modifiers = [];

  // first get modifiers on the product
  if (product.get('modifier_groups')) {
    const modifierGroups = product.get('modifier_groups').sortBy(group => group.get('sort_order'));
    if (modifierGroups) {
      modifierGroups.map(modifierGroup =>
        modifierGroup
          .get('modifiers')
          .sortBy(group => group.get('sort_order'))
          .forEach(modifier => {
            if (modifier.get('display_type') !== 'base' || inclBaseModifier) {
              modifiers.push({ ...modifier.toJS(), groupId: modifierGroup.get('id') });
            }
          })
      );
    }
  }

  // then get modifiers if the product is a combo, where each combo group is treated as a modifier
  const comboGroups = getProductComboGroupsAsModifiers(product);
  if (comboGroups) {
    comboGroups.forEach(comboGroup => {
      modifiers.push(comboGroup);
      // For all chosen combo group products that belong to this comboGroup also add their modifiers

      selectedModifiers
        .filter(chosen => chosen.get('combo_group') === comboGroup.id)
        .forEach(chosen => {
          const additionalModifiers = getProductModifiers(
            chosen.getIn(['values', 0, 'product']),
            false,
            [],
            false,
            serviceId
          );
          if (additionalModifiers && additionalModifiers.size) {
            // Attach parent_id to each additional modifier
            const additionalWithParent = additionalModifiers.map(mod =>
              mod.set('parent_combo_group_id', comboGroup.id)
            );

            // Insert additionalModifiers (converted to JS) after the current group
            modifiers.push(...additionalWithParent.toJS());
          }
        });
    });
  }

  const filteredModifiers = filterModifierValuesByService(
    filterModifierValues(modifiers, isEposIntegrated),
    serviceId
  );
  return fromJS(filteredModifiers);
};

export const getProductComboGroups = (product = false) => {
  if (!product.get('combo')) return new List();
  return product
    .get('combo')
    .get('combo_groups')
    .sortBy(group => group.get('sort_order'));
};

export const getSelectedBaseModifierProduct = modifiers => {
  const baseModifier = modifiers && modifiers.find(mod => mod.getIn(['modifier', 'display_type']) === 'base');
  return baseModifier?.getIn(['values', 0, 'product']);
};

export const selectMenuTypeId = state => state.getIn(['browse', 'menuTypeId']) || undefined;

export const selectMenuType = state => selectMenuTypeById(state, selectMenuTypeId(state));

export const selectServiceId = state => state.getIn(['browse', 'serviceId']);
export const selectMenuId = state => state.getIn(['browse', 'menuId']);

export const selectLowestAvailableMenuItemsForProducts = (state, products, menuTypeId, serviceId) => {
  if (!products || !menuTypeId || !serviceId) return new List();
  return products.map(product =>
    selectLowestAvailableMenuItemForProduct(state, product, menuTypeId, serviceId)
  );
};

export const selectLowestAvailableMenuItemForProduct = (state, product, menuTypeId, serviceId) => {
  const activeMenus = selectActiveMenus(state, menuTypeId, serviceId);

  if (!activeMenus || !product) return undefined;

  const lowestMenuInformation = activeMenus.reduce((bestProduct, menu) => {
    const bestCurrentProduct = getBestPriceInSections(menu, product.get('id'), menuTypeId, serviceId);
    if (bestProduct) {
      if (findPrice(bestCurrentProduct, menuTypeId) < findPrice(bestProduct, menuTypeId)) {
        return bestCurrentProduct;
      }
      return bestProduct;
    }
    return bestCurrentProduct;
  }, undefined);
  if (lowestMenuInformation) {
    return lowestMenuInformation.set('product', product);
  }
  return undefined;
};

export const getBestPriceInSections = (menu, productId, menuTypeId, serviceId) => {
  if (!isInService(menu, serviceId)) return undefined;
  return menu.get('sections').reduce((foundSectionProduct, section) => {
    const newSectionProduct = section
      .get('section_products')
      .find(sectionProduct => sectionProduct.get('productId') === productId);

    const foundPrice = findPrice(newSectionProduct, menuTypeId);
    if (foundPrice && foundPrice) {
      if (foundSectionProduct) {
        const previousProductPrice = findPrice(foundSectionProduct, menuTypeId);
        if (foundPrice < previousProductPrice) return newSectionProduct;
        if (foundPrice > previousProductPrice) return foundSectionProduct;
      }
      return newSectionProduct;
    }
    return foundSectionProduct;
  }, undefined);
};

export const isInService = (menu, serviceId) => {
  if (
    menu &&
    (menu.get('services').size === 0 || menu.get('services').indexOf(parseInt(serviceId, 10)) !== -1)
  )
    return true;
  return false;
};

export const findPrice = (sectionProduct, menuTypeId) => {
  const sectionProductResult =
    sectionProduct && sectionProduct.get('prices').find(price => price.get('menu_type') === menuTypeId);
  if (sectionProductResult) {
    return sectionProductResult.get('price');
  }
  return undefined;
};

/**
 * Be aware, this method essentially returns the item display price, as it takes into account
 * the setting of the "Show prices excluding tax" feature.
 */
export const getItemPrice = (state, price, taxCategories = new List()) => {
  if (!taxCategories?.size) {
    return price;
  }
  let includedTaxTotal = 0;
  let excludedTaxTotal = 0;
  taxCategories.forEach(category => {
    const taxCategoryId = typeof category === 'object' ? category.get('id') : category;
    const taxCategory =
      category.get && category.get('rates') ? category : getTaxCategoryById(state, taxCategoryId);

    if (!taxCategory) return;

    const rate = taxCategory.getIn(['rates', 0, 'amount']);
    const includedInPrice = taxCategory.getIn(['rates', 0, 'included_in_price']);

    const calculatedTax = getTax(rate, price, includedInPrice);

    if (includedInPrice) {
      includedTaxTotal += calculatedTax;
    } else {
      excludedTaxTotal += calculatedTax;
    }
  });

  return shouldShowPriceExcludingTax(state) ? price - includedTaxTotal : price + excludedTaxTotal;
};

export const getModifierPrice = (state, modifier, taxCategories = new List(), modifierDisplayType) => {
  modifier = fromJS(modifier);
  let price = modifier.get('price_adjustment');
  if (modifierDisplayType === 'base') {
    price = modifier.getIn(['product', 'price']);
    taxCategories = modifier.getIn(['product', 'tax_categories']);
  }

  const individualPrice = getItemPrice(state, price, taxCategories);

  return individualPrice && individualPrice * (modifier.get('quantity') || 1);
};

export const getModifiersPriceForTrackEvent = modifiers => {
  if (modifiers) {
    if (modifiers.size === 0) {
      return null;
    }
    const priceAdj = modifiers.flatMap(el => el.get('values')).map(el => el.getIn(['price_adjustment']));
    const modsPrice =
      priceAdj && priceAdj.size > 0
        ? priceAdj.reduce((accumulator, currentValue) => accumulator + currentValue, 0) / 100
        : 0;
    return modsPrice;
  }

  return null;
};

export const getTax = (taxRate, basePrice, isTaxIncluded = false) => {
  const rate = parseFloat(taxRate);

  if (isTaxIncluded) {
    // Tax is already included in basePrice; extract the tax portion
    return Math.round(basePrice - basePrice / (1 + rate));
  }

  // Tax is added on top of basePrice
  return Math.round(rate * basePrice);
};

export const selectVenuePath = state => state.getIn(['venue', 'routes', 0, 'path'], false);

export const getBaseProductForItem = (product, item) => {
  if (item.get('base_product_id') === item.get('productId')) return null;

  return product
    .get('modifier_groups')
    ?.flatMap(group => group.get('modifiers', List()))
    .flatMap(mod => mod.get('values', List()))
    .map(value => value.get('product'))
    .find(p => p && p.get('id') === item.get('productId'), null, null);
};

/**
 * Sort modifiers so base modifiers are first, then non-base modifiers.
 */
export const sortModifiers = modifiers =>
  modifiers.sort((a, b) => {
    const aBase = a.get('display_type') === 'base';
    const bBase = b.get('display_type') === 'base';
    // eslint-disable-next-line no-nested-ternary
    return aBase === bBase ? 0 : aBase ? -1 : 1;
  });

export const mergeBaseAndProductModifiers = (baseModifiers, productModifiers) => {
  if (!baseModifiers) return productModifiers;
  const combined = baseModifiers.concat(productModifiers ?? new List());
  return sortModifiers(combined);
};

export const getProductAndModifiersFromItem = (state, item, showBaseModifiers, selectedModifiers = []) => {
  const product = getProductById(state, item.get('base_product_id') || item.get('productId'));
  const isEposIntegrated = selectEPOSIntegrated(state);
  const serviceId = selectServiceId(state);

  const productModifiers = getProductModifiers(
    product,
    showBaseModifiers,
    selectedModifiers,
    isEposIntegrated,
    serviceId
  );

  const baseProduct = getBaseProductForItem(product, item);

  if (baseProduct) {
    const baseProductModifiers = getProductModifiers(
      baseProduct,
      showBaseModifiers,
      undefined,
      undefined,
      serviceId
    );
    const merged = mergeBaseAndProductModifiers(baseProductModifiers, productModifiers);
    return { product, productModifiers: merged };
  }

  return { product, productModifiers };
};
